import React, { useMemo, useState } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import { faqCategories } from "../data/helpCenterFaqs";
import useScrollReveal from "../hooks/useScrollReveal";
import "./HelpCenter.css";

function AccordionQuestion({ categoryId, item, index, openId, onToggle }) {
  const itemId = `${categoryId}-${index}`;
  const answerId = `faq-answer-${itemId}`;
  const isOpen = openId === itemId;

  return (
    <article className={`faq-row${isOpen ? " is-open" : ""}`}>
      <h3>
        <button
          type="button"
          onClick={() => onToggle(itemId)}
          aria-expanded={isOpen}
          aria-controls={answerId}
        >
          <span>{item.question}</span>
          <KeyboardArrowDownRoundedIcon aria-hidden="true" />
        </button>
      </h3>
      <div className="faq-answer" id={answerId} hidden={!isOpen}>
        <p>{item.answer}</p>
      </div>
    </article>
  );
}

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  useScrollReveal();

  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const visibleCategories = useMemo(() => {
    if (!normalizedQuery) return faqCategories;

    return faqCategories
      .map((category) => ({
        ...category,
        questions: category.questions.filter((item) =>
          `${category.title} ${item.question} ${item.answer}`
            .toLocaleLowerCase()
            .includes(normalizedQuery),
        ),
      }))
      .filter((category) => category.questions.length > 0);
  }, [normalizedQuery]);

  const resultCount = visibleCategories.reduce(
    (total, category) => total + category.questions.length,
    0,
  );

  const scrollToCategory = (categoryId) => {
    document.getElementById(`faq-${categoryId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="faq-page">
      <SiteHeader />

      <section className="faq-hero tl-reveal is-visible">
        <p className="faq-eyebrow">Help Center</p>
        <h1>Frequently Asked Questions</h1>
        <p className="faq-intro">
          Everything you need to know about your trilLOAN account.
        </p>
        <label className="faq-search">
          <SearchRoundedIcon aria-hidden="true" />
          <span className="sr-only">Search frequently asked questions</span>
          <input
            type="search"
            placeholder="Search questions or topics"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
      </section>

      <section className="faq-content" aria-label="Frequently asked questions">
        {!normalizedQuery && (
          <nav className="faq-category-nav" aria-label="FAQ categories">
            {faqCategories.map((category) => (
              <button
                type="button"
                key={category.id}
                onClick={() => scrollToCategory(category.id)}
              >
                {category.title}
              </button>
            ))}
          </nav>
        )}

        {normalizedQuery && (
          <p className="faq-result-count" aria-live="polite">
            {resultCount} {resultCount === 1 ? "answer" : "answers"} found for “{searchQuery.trim()}”
          </p>
        )}

        {visibleCategories.length > 0 ? (
          <div className="faq-sections">
            {visibleCategories.map((category) => (
              <section
                className="faq-category tl-reveal is-visible"
                id={`faq-${category.id}`}
                key={category.id}
              >
                <div className="faq-category-heading">
                  <p>{category.title}</p>
                  <span>{category.copy}</span>
                </div>
                <div className="faq-list">
                  {category.questions.map((item) => {
                    const sourceIndex = faqCategories
                      .find((source) => source.id === category.id)
                      .questions.findIndex((source) => source.question === item.question);

                    return (
                      <AccordionQuestion
                        categoryId={category.id}
                        item={item}
                        index={sourceIndex}
                        openId={openId}
                        onToggle={(itemId) =>
                          setOpenId((current) => (current === itemId ? null : itemId))
                        }
                        key={item.question}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="faq-empty-state">
            <h2>No matching questions</h2>
            <p>Try another keyword or clear your search to browse every category.</p>
            <button type="button" onClick={() => setSearchQuery("")}>Clear search</button>
          </div>
        )}
      </section>

      <section className="faq-contact tl-reveal">
        <div>
          <p>Still have questions?</p>
          <h2>We’re here to help.</h2>
          <span>Reach our support team through the trilLOAN app, Live Chat, or email.</span>
        </div>
        <a href="mailto:corporate@trilloan.com">Contact Support</a>
      </section>

      <SiteFooter />
    </main>
  );
}
